import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { AlertTriangle, ChevronRight, Download, FileText, Loader2, Plus, RefreshCw, Search, Wand2, X } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { formatDay, todayIso } from "../lib/finance";
import { VIEWS, canWrite, datesHint, partiesText, recordsError } from "../lib/records";
import { ClassificationBadge, StateBadge } from "../components/records/RecordBits";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Skeleton } from "../components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

const ALL = "__all__";
const PAGE_SIZE = 50;

function useFilters() {
  const [params, setParams] = useSearchParams();
  const filters = {
    view: VIEWS.some((v) => v.id === params.get("view")) ? params.get("view") : "current",
    type: params.get("type") || "",
    party: params.get("party") || "",
    search: params.get("q") || "",
  };
  const update = useCallback((changes) => {
    setParams((prev) => {
      const next = new URLSearchParams(prev);
      for (const [key, value] of Object.entries(changes)) {
        const name = { search: "q" }[key] || key;
        if (value === "" || value == null || (key === "view" && value === "current")) next.delete(name);
        else next.set(name, value);
      }
      return next;
    }, { replace: true });
  }, [setParams]);
  return [filters, update];
}

export default function Records() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [filters, update] = useFilters();
  const [searchInput, setSearchInput] = useState(filters.search);
  const [data, setData] = useState({ items: [], total: 0 });
  const [types, setTypes] = useState([]);
  const [phase, setPhase] = useState("loading");
  const [error, setError] = useState("");
  const [loadingMore, setLoadingMore] = useState(false);
  const [exporting, setExporting] = useState(false);
  const seq = useRef(0);
  const today = todayIso();

  useEffect(() => {
    const t = setTimeout(() => { if (searchInput !== filters.search) update({ search: searchInput.trim() }); }, 300);
    return () => clearTimeout(t);
  }, [searchInput]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { api.get("/records/types").then(({ data: body }) => setTypes(body.items)).catch(() => {}); }, []);

  const query = useMemo(() => {
    const view = VIEWS.find((v) => v.id === filters.view);
    const out = { limit: PAGE_SIZE, ...view.params };
    if (filters.type) out.type_code = filters.type;
    if (filters.party) out.party_ref = filters.party;
    if (filters.search) out.search = filters.search;
    return out;
  }, [filters.view, filters.type, filters.party, filters.search]);

  const load = useCallback(async () => {
    const mine = ++seq.current;
    setPhase("loading");
    try {
      const { data: body } = await api.get("/records/documents", { params: query });
      if (mine !== seq.current) return;
      setData(body);
      setPhase("ready");
    } catch (e) {
      if (mine !== seq.current) return;
      setError(recordsError(e, "Impossible de charger le registre."));
      setPhase("error");
    }
  }, [query]);
  useEffect(() => { load(); return () => { seq.current += 1; }; }, [load]);

  const loadMore = async () => {
    setLoadingMore(true);
    try {
      const { data: body } = await api.get("/records/documents", { params: { ...query, skip: data.items.length } });
      setData((prev) => ({ ...body, items: [...prev.items, ...body.items.filter((i) => !prev.items.some((p) => p.id === i.id))] }));
    } catch (e) {
      toast.error(recordsError(e, "Impossible de charger la suite."));
    } finally {
      setLoadingMore(false);
    }
  };

  const exportCsv = async () => {
    setExporting(true);
    try {
      const { data: blob } = await api.get("/records/export.csv", { responseType: "blob" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "registre-documents.csv";
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Registre exporté.");
    } catch (e) {
      toast.error(recordsError(e, "Export impossible."));
    } finally {
      setExporting(false);
    }
  };

  const filtered = !!(filters.type || filters.party || filters.search);
  const reset = () => { setSearchInput(""); update({ type: "", party: "", search: "" }); };

  return (
    <div className="space-y-4" data-testid="records-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h1 className="font-display font-bold text-2xl text-slate-900">Documents et contrats</h1>
          <p className="text-sm text-slate-500">Le registre des contrats et documents de Jamora : qui les a signés, quand ils expirent, où est le fichier.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          {canWrite(user, "records:export") && (
            <Button variant="outline" onClick={exportCsv} disabled={exporting} data-testid="records-export">
              {exporting ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <Download size={14} className="mr-1.5" />}Exporter
            </Button>
          )}
          {canWrite(user, "records:create") && (
            <Button variant="outline" asChild data-testid="records-generate">
              <Link to="/documents/generer"><Wand2 size={14} className="mr-1.5" />Générer depuis un modèle</Link>
            </Button>
          )}
          {canWrite(user, "records:create") && (
            <Button asChild data-testid="records-new">
              <Link to={filters.party ? `/documents/nouveau?prospect=${filters.party}` : "/documents/nouveau"}><Plus size={16} className="mr-1.5" />Nouveau document</Link>
            </Button>
          )}
        </div>
      </div>

      <div className="flex gap-1 flex-wrap" role="tablist" aria-label="Vues du registre">
        {VIEWS.map((v) => (
          <button key={v.id} type="button" role="tab" aria-selected={filters.view === v.id} onClick={() => update({ view: v.id })}
            data-testid={`records-view-${v.id}`}
            className={`rounded-full px-3 py-1.5 text-sm font-medium whitespace-nowrap ${filters.view === v.id ? "bg-jamora text-white" : "bg-white text-slate-600 border border-slate-200 hover:bg-slate-50"}`}>
            {v.label}
          </button>
        ))}
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative basis-full sm:basis-auto sm:flex-1 min-w-0 md:max-w-xs">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" aria-hidden="true" />
          <Input value={searchInput} onChange={(e) => setSearchInput(e.target.value)} placeholder="Numéro, titre ou partie…" className="pl-9 h-9"
            aria-label="Rechercher un document" data-testid="records-search" />
        </div>
        <Select value={filters.type || ALL} onValueChange={(v) => update({ type: v === ALL ? "" : v })}>
          <SelectTrigger className="h-9 w-full sm:w-56" aria-label="Type de document" data-testid="records-type-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>Tous les types</SelectItem>
            {types.map((t) => <SelectItem key={t.code} value={t.code}>{t.label}</SelectItem>)}
          </SelectContent>
        </Select>
        {filters.party && <span className="inline-flex items-center rounded-full border border-slate-200 bg-white px-2.5 py-1 text-xs text-slate-600">Une partie précise</span>}
        {filtered && <Button variant="ghost" size="sm" className="h-9 text-slate-600" onClick={reset}><X size={14} className="mr-1" />Effacer</Button>}
      </div>

      {phase === "loading" && <div className="space-y-2"><Skeleton className="h-12 w-full" /><Skeleton className="h-12 w-full" /><Skeleton className="h-12 w-full" /></div>}
      {phase === "error" && (
        <Card className="p-6 text-center space-y-3">
          <AlertTriangle className="mx-auto text-amber-500" size={20} />
          <p className="text-sm text-slate-700">{error}</p>
          <Button variant="outline" onClick={load}><RefreshCw size={14} className="mr-1.5" />Réessayer</Button>
        </Card>
      )}
      {phase === "ready" && data.items.length === 0 && (
        <Card className="p-8 text-center space-y-2" data-testid="records-empty">
          <FileText className="mx-auto text-slate-300" size={26} />
          {filtered || filters.view !== "current" ? (
            <><h3 className="font-medium text-slate-900">Aucun document ne correspond</h3>
              {filtered && <Button variant="outline" size="sm" onClick={reset}>Effacer les filtres</Button>}</>
          ) : (
            <><h3 className="font-medium text-slate-900">Aucun document pour le moment</h3>
              <p className="text-sm text-slate-500 max-w-md mx-auto">Enregistrez un contrat, une attestation ou un document reçu : vous saurez où il est et quand il expire.</p></>
          )}
        </Card>
      )}
      {phase === "ready" && data.items.length > 0 && (
        <>
          <Card className="hidden md:block overflow-hidden" data-testid="records-table">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Document</TableHead><TableHead>Parties</TableHead><TableHead>État</TableHead>
                  <TableHead className="hidden lg:table-cell">Échéance</TableHead><TableHead className="hidden xl:table-cell">Responsable</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.items.map((d) => {
                  const hint = datesHint(d, today);
                  return (
                    <TableRow key={d.id} className="cursor-pointer" onClick={() => navigate(`/documents/${d.id}`)} data-testid={`records-row-${d.number}`}>
                      <TableCell>
                        <Link to={`/documents/${d.id}`} className="font-medium text-slate-900 hover:underline" onClick={(e) => e.stopPropagation()}>{d.title}</Link>
                        <div className="flex flex-wrap items-center gap-1.5 text-xs text-slate-500">
                          <span className="tabular-nums">{d.number}</span><span>· {d.type_label}</span>
                          {d.amendment_number && <span>· avenant n° {d.amendment_number}</span>}
                          <ClassificationBadge value={d.classification} />
                        </div>
                      </TableCell>
                      <TableCell className="text-sm text-slate-700 max-w-xs truncate">{partiesText(d.parties)}</TableCell>
                      <TableCell><StateBadge state={d.state} /></TableCell>
                      <TableCell className="hidden lg:table-cell text-sm">
                        <span className="tabular-nums">{d.expiry_date ? formatDay(d.expiry_date) : "—"}</span>
                        {hint && <div className="text-xs text-amber-700">{hint}</div>}
                      </TableCell>
                      <TableCell className="hidden xl:table-cell text-sm text-slate-600">{d.responsible_name || "—"}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Card>
          <Card className="md:hidden divide-y divide-slate-100" data-testid="records-cards">
            {data.items.map((d) => {
              const hint = datesHint(d, today);
              return (
                <Link key={d.id} to={`/documents/${d.id}`} className="flex items-start gap-3 px-4 py-3" data-testid={`records-card-${d.number}`}>
                  <div className="min-w-0 flex-1 space-y-1">
                    <div className="font-medium text-slate-900 break-words">{d.title}</div>
                    <div className="text-xs text-slate-500">{d.number} · {d.type_label}</div>
                    <div className="text-xs text-slate-600 break-words">{partiesText(d.parties)}</div>
                    <div className="flex flex-wrap items-center gap-2"><StateBadge state={d.state} /><ClassificationBadge value={d.classification} /></div>
                    {hint && <div className="text-xs text-amber-700">{hint}</div>}
                  </div>
                  <ChevronRight size={16} className="text-slate-300 shrink-0 mt-1" />
                </Link>
              );
            })}
          </Card>
          <div className="flex items-center justify-between gap-3 text-xs text-slate-500">
            <span data-testid="records-count">{data.items.length} sur {data.total} document{data.total > 1 ? "s" : ""}</span>
            {data.items.length < data.total && (
              <Button variant="outline" size="sm" onClick={loadMore} disabled={loadingMore}>{loadingMore && <Loader2 size={14} className="mr-1.5 animate-spin" />}Charger plus</Button>
            )}
          </div>
        </>
      )}
    </div>
  );
}
